import { createContext, useState } from "react";

import { NodeWithChildren } from "domhandler";

import { removeDuplicatedObjects } from "pages/[tenant]/g/utils/removeDuplicatedObjects";

import { INotification } from "shared/components/organisms/AppNotification/@types";
import { useUserNotificationsService } from "shared/components/organisms/AppNotification/hooks/useUserNotificationsService";
import useNextLoadingProgress from "shared/utils/hooks/useNextLoadingProgress";

interface ITopbarContext {
  scrollIsOnTop: boolean;
  setScrollIsOnTop: React.Dispatch<React.SetStateAction<boolean>>;
  pageIsLoading: boolean;
  sideMenuIsOpen: boolean;
  footerMobileMenuIsOpen: boolean;
  openSideMenu: () => void;
  closeSideMenu: () => void;
  openFooterMobileMenu: () => void;
  closeFooterMobileMenu: () => void;
  notificationsIsOpen: boolean;
  openNotifications: () => void;
  closeNotifications: () => void;
  searchIsOpen: boolean;
  openSearch: () => void;
  closeSearch: () => void;

  isSecondaryMenuContainerOpen: boolean;
  openSecondaryMenuContainer: () => void;
  closeSecondaryMenuContainer: () => void;

  isContentMenuOpen: boolean;
  closeContentMenu: () => void;
  openContentMenu: () => void;

  hasNewNotifications: boolean;
  setHasNewNotifications: React.Dispatch<React.SetStateAction<boolean>>;

  alreadyLoadedNewNotifications: boolean;
  setAlreadyLoadedNewNotifications: React.Dispatch<
    React.SetStateAction<boolean>
  >;

  notifications: INotification<any>[];
  markAllNotificationAsReadInInterface: () => void;
  addNotificationsToList: (newNotifications: INotification<any>[]) => void;
  addNewNotificationsToList: (newNotifications: INotification<any>[]) => void;

  closeAllFooterMobileOptions: VoidFunction;
}

export const TopbarContext = createContext({} as ITopbarContext);

export const TopbarContextProvider = ({ children }: NodeWithChildren) => {
  const [scrollIsOnTop, setScrollIsOnTop] = useState(false);
  const pageIsLoading = useNextLoadingProgress();

  const [sideMenuIsOpen, setSideMenuIsOpen] = useState(false);
  const openSideMenu = () => setSideMenuIsOpen(true);
  const closeSideMenu = () => setSideMenuIsOpen(false);

  const [isSecondaryMenuContainerOpen, setIsSecondaryMenuContainerOpen] =
    useState(false);
  const openSecondaryMenuContainer = () =>
    setIsSecondaryMenuContainerOpen(true);
  const closeSecondaryMenuContainer = () =>
    setIsSecondaryMenuContainerOpen(false);

  const [searchIsOpen, setSearchIsOpen] = useState(false);
  const closeSearch = () => setSearchIsOpen(false);
  const openSearch = () => {
    if (notificationsIsOpen) closeNotifications();
    if (isContentMenuOpen) closeContentMenu();
    if (footerMobileMenuIsOpen) closeFooterMobileMenu();
    setSearchIsOpen(true);
  };

  const [footerMobileMenuIsOpen, setFooterMobileMenuIsOpen] = useState(false);
  const closeFooterMobileMenu = () => {
    if (isContentMenuOpen) closeContentMenu();
    setFooterMobileMenuIsOpen(false);
  };

  const openFooterMobileMenu = () => {
    if (notificationsIsOpen) closeNotifications();
    if (isContentMenuOpen) closeContentMenu();
    if (searchIsOpen) closeSearch();
    setFooterMobileMenuIsOpen(true);
  };

  const closeAllFooterMobileOptions = () => {
    if (notificationsIsOpen) closeNotifications();
    if (isContentMenuOpen) closeContentMenu();
    if (searchIsOpen) closeSearch();
    if (footerMobileMenuIsOpen) setFooterMobileMenuIsOpen(false);
  };

  const [isContentMenuOpen, setIsContentMenuOpen] = useState(false);
  const closeContentMenu = () => setIsContentMenuOpen(false);
  const openContentMenu = () => setIsContentMenuOpen(true);

  const { markAllNotificationsAsRead } = useUserNotificationsService();
  const [notifications, setNotifications] =
    useState<INotification<any>[]>(null);
  const [hasNewNotifications, setHasNewNotifications] = useState(false);
  const [notificationsIsOpen, setNotificationsIsOpen] = useState(false);
  const [alreadyLoadedNewNotifications, setAlreadyLoadedNewNotifications] =
    useState(false);

  const markAllNotificationAsReadInInterface = (): void => {
    setNotifications((prev) =>
      (prev ?? [])?.map((notification) => ({
        ...(notification ?? ({} as INotification<any>)),
        read_at: new Date()?.getTime()?.toString(),
      })),
    );
  };

  const closeNotifications = async () => {
    setNotificationsIsOpen(false);

    if (!hasNewNotifications) return;

    const markedAsReadSuccessfully = await markAllNotificationsAsRead();
    if (markedAsReadSuccessfully) {
      markAllNotificationAsReadInInterface();
      setHasNewNotifications(false);
    }
  };

  const addNotificationsToList = (
    newNotifications: INotification<any>[],
  ): void =>
    setNotifications((prev) =>
      removeDuplicatedObjects([...(prev ?? []), ...(newNotifications ?? [])]),
    );

  const addNewNotificationsToList = (
    newNotifications: INotification<any>[],
  ): void =>
    setNotifications((prev) =>
      removeDuplicatedObjects([...(newNotifications ?? []), ...(prev ?? [])]),
    );

  const openNotifications = () => {
    if (footerMobileMenuIsOpen) closeFooterMobileMenu();
    if (isContentMenuOpen) closeContentMenu();
    if (searchIsOpen) closeSearch();

    setNotificationsIsOpen(true);
  };

  return (
    <TopbarContext.Provider
      value={{
        scrollIsOnTop,
        setScrollIsOnTop,
        sideMenuIsOpen,
        footerMobileMenuIsOpen,
        openSideMenu,
        closeSideMenu,
        openFooterMobileMenu,
        closeFooterMobileMenu,
        pageIsLoading,
        notificationsIsOpen,
        openNotifications,
        closeNotifications,
        searchIsOpen,
        openSearch,
        closeSearch,

        isContentMenuOpen,
        closeContentMenu,
        openContentMenu,

        isSecondaryMenuContainerOpen,
        openSecondaryMenuContainer,
        closeSecondaryMenuContainer,

        hasNewNotifications,
        setHasNewNotifications,

        notifications,
        markAllNotificationAsReadInInterface,
        addNotificationsToList,
        addNewNotificationsToList,

        alreadyLoadedNewNotifications,
        setAlreadyLoadedNewNotifications,

        closeAllFooterMobileOptions,
      }}
    >
      {children}
    </TopbarContext.Provider>
  );
};
